import React from 'react'
import { Checkbox, Spinner, Text, csx, cx, dataAttr } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'

import { ProductImage, ProductName } from '../../../../../../core/components'
import { resizeItemImageResolution } from '../../../../../../core/utils/orderForm'
import type { GiftItem } from '../../../../../../core/requests/checkout'

export type GiftListItemProps = {
  item: GiftItem
  isLoading: boolean
  isDisabled: boolean
  isSelectable: boolean
  onSelectItem: (item: GiftItem) => void
} & React.ComponentProps<'div'>

export const GiftListItem = ({
  item,
  isLoading,
  isDisabled,
  isSelectable,
  onSelectItem,
  // TODO: this error popped up when moving the checkout-instore project
  // to checkout-instore-legacy, but needs to be fixed at some point
  // eslint-disable-next-line react/prop-types
  className = '',
}: GiftListItemProps) => {
  const handleSelectItem = () => {
    if (isDisabled || !isSelectable) return

    onSelectItem(item)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSelectItem()
    }
  }

  return (
    <div
      data-testid="gift-list-item"
      role="button"
      tabIndex={0}
      onClick={handleSelectItem}
      onKeyDown={handleKeyDown}
      aria-disabled={isDisabled}
      className={cx(
        csx({
          display: 'flex',
          flexDirection: 'row',
          gap: '$space-4',
          width: '18rem',
          minWidth: '18rem',
          padding: '$space-4',
          border: item.isSelected ? '$mainSelected' : '$neutral',
          borderRadius: '0.5rem',
          cursor: isDisabled || !isSelectable ? 'default' : 'pointer',
          filter: isDisabled ? 'grayscale(100%)' : undefined,
          ':hover': isDisabled
            ? undefined
            : {
                border: item.isSelected
                  ? '$mainSelected'
                  : '$form.neutralHover',
              },
        }),
        className
      )}
    >
      <div className={csx({ width: '5rem' })}>
        <ProductImage
          src={resizeItemImageResolution(item.imageUrl)}
          alt={item.name}
        />
      </div>
      <div
        className={csx({
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
        })}
      >
        <ProductName
          className={csx({
            marginTop: '0rem !important',
            [dataAttr('variant', 'action1')]: {
              color: isDisabled ? '$gray40' : undefined,
            },
          })}
        >
          {item.name}
        </ProductName>
        <div
          className={csx({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}
        >
          <Text tone="positive">
            <FormattedMessage id="freePriceLabel" />
          </Text>
          {isLoading ? (
            <Spinner
              data-testid="gift-list-item-loading"
              size={22}
              className={csx({ color: '$blue40', marginRight: '0.5rem' })}
            />
          ) : (
            <Checkbox
              data-testid="gift-list-item-checkbox"
              state={{ value: !!item.isSelected, setValue: () => {} }}
              disabled={isDisabled}
              className={csx({
                borderRadius: '1rem !important',
                background: isDisabled ? 'white !important' : undefined,
              })}
            />
          )}
        </div>
      </div>
    </div>
  )
}
